import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Check } from 'lucide-react';

const plans = [
  {
    id: '88d1cdbf-f9da-49ea-a121-72010bbdecbb',
    name: 'Monthly Premium',
    price: '₹2,500',
    amount: 250000,
    benefits: [
      '30 days full access',
      'Access to verified friends',
      'View profiles & Send requests',
      'Chat & connect',
    ],
    cta: 'Select Monthly',
    to: '/payment',
    primary: true,
  },
  {
    id: 'cdbaf917-2fdc-42b9-aab5-8d5f2255a15f',
    name: 'Yearly Premium',
    price: '₹5,000',
    amount: 500000,
    benefits: [
      '365 days full access',
      'Best value for long-term',
      'Priority verification',
      'All premium features',
    ],
    cta: 'Select Yearly',
    to: '/payment',
    primary: false,
  },
];

const PricingSection = () => {
  const { userProfile } = useAuth();

  return (
    <section className="py-16 md:py-24 bg-slate-900" id="pricing">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-heading font-black text-3xl sm:text-4xl text-white text-center mb-4">
          Membership Plans
        </h2>
        <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
          Choose the plan that fits your needs. Upgrade to premium to start chatting and connecting with verified friends.
        </p>
        
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan) => {
            const isActive = userProfile?.subscriptionStatus === 'premium' && userProfile?.subscription_plan === plan.id;
            const hasAnyPremium = userProfile?.subscriptionStatus === 'premium';

            return (
              <div
                key={plan.name}
                className={`relative rounded-3xl border-2 p-8 transition-all duration-300 ${
                  plan.primary 
                    ? 'bg-slate-800/80 border-brand-pink shadow-[0_0_40px_rgba(236,72,153,0.1)]' 
                    : 'bg-slate-800/40 border-slate-700 hover:border-slate-500'
                } ${isActive ? 'ring-4 ring-green-500/20' : ''}`}
              >
                {isActive && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-green-500 text-white text-[10px] font-black uppercase tracking-widest shadow-lg">
                    Current Plan
                  </div>
                )}
                
                <h3 className="font-heading font-bold text-xl text-white">{plan.name}</h3>
                <div className="mt-4 flex items-baseline gap-1">
                   <span className="text-4xl font-black text-white">{plan.price}</span>
                   <span className="text-slate-500 text-sm font-medium">/{plan.id.includes('cdba') ? 'year' : 'month'}</span>
                </div>

                <ul className="mt-8 space-y-4">
                  {plan.benefits.map((b, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-300">
                      <div className="flex items-center justify-center w-5 h-5 rounded-full bg-green-500/10 text-green-500">
                        <Check className="h-3 w-3" />
                      </div>
                      <span className="text-sm font-medium">{b}</span>
                    </li>
                  ))}
                </ul>

                {isActive ? (
                   <div className="mt-10 w-full py-4 rounded-2xl bg-green-500/20 border border-green-500/30 text-green-400 font-bold text-center flex items-center justify-center gap-2">
                     <Check className="h-5 w-5" />
                     Currently Active
                   </div>
                ) : (
                  <Link
                    to={plan.to}
                    state={{ selectedPlanId: plan.id }}
                    className={`mt-10 block w-full py-4 rounded-2xl text-center font-black transition-all transform active:scale-[0.98] ${
                      plan.primary
                        ? 'bg-brand-pink text-white hover:bg-brand-pink-dark shadow-xl shadow-brand-pink/20'
                        : 'bg-slate-700 text-white hover:bg-slate-600'
                    } ${hasAnyPremium ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
                    onClick={(e) => {
                      if (hasAnyPremium) {
                        e.preventDefault();
                      }
                    }}
                  >
                    {hasAnyPremium ? 'Already Subscribed' : plan.cta}
                  </Link>
                )}
                
                {hasAnyPremium && !isActive && (
                   <p className="mt-4 text-[10px] text-slate-500 text-center font-bold uppercase tracking-widest">
                     You already have an active subscription
                   </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
